import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Eye, FileSignature, Loader2, Send, TriangleAlert } from "lucide-react";
import { api } from "../../lib/api";
import { contractActions } from "../../lib/contractStatus";
import ContractCard, { BTN_PRIMARY, BTN_SECONDARY, errorDetail, openBlob } from "./ContractCard";
import { contractTitle, isFpedContract } from "../../lib/regime";

/**
 * The course contract for this participant: check, preview, send, follow.
 *
 * "Mod de probă" sends the same contract to the operator instead of the
 * participant — with the production key, so it costs a credit and the FPED signer's
 * signature is real; every page says PROBĂ. Probes have their own slot and
 * never block the real contract.
 *
 * Previews and downloads carry the CNP: fetched as blobs, opened from memory,
 * the object URL released shortly after.
 */
export default function ContractPanel({ enrollmentId }) {
  const [probe, setProbe] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async (mode) => {
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/contract`, { params: { probe: mode } });
      setState(r.data);
    } catch (err) {
      setState({ error: errorDetail(err, "Nu am putut verifica datele pentru contract.") });
    }
  }, [enrollmentId]);

  useEffect(() => { setState(null); load(probe); }, [load, probe]);

  const contract = state?.contract;
  const actions = contractActions(contract);

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      const r = await fn();
      if (success) toast.success(typeof success === "function" ? success(r.data) : success);
      await load(probe);
    } catch (err) {
      toast.error(errorDetail(err, "Acțiunea nu a reușit."));
      await load(probe);
    } finally {
      setBusy(null);
    }
  };

  const send = () => {
    const question = probe
      ? "Trimiți contractul DE PROBĂ către tine? Consumă 1 credit eSemneaza."
      : "Trimiți contractul participantului pentru semnare? Consumă 1 credit eSemneaza și nu poate fi retras decât prin anulare.";
    if (!window.confirm(question)) return;
    run("send", () => api.post(`/admin/enrollments/${enrollmentId}/contract/send`, null, { params: { probe } }),
      (d) => `Contractul ${d.contract.number} a fost trimis${probe ? " (probă)" : ""}.`);
  };

  const preview = async () => {
    setBusy("preview");
    try {
      await openBlob(
        () => api.get(`/admin/enrollments/${enrollmentId}/contract/preview`, { responseType: "blob" }),
        "contract-previzualizare.pdf",
      );
    } catch (err) {
      toast.error(err?.response?.status === 409
        ? "Contractul nu poate fi generat încă. Verifică motivele din fișă."
        : "Nu am putut genera previzualizarea.");
    } finally {
      setBusy(null);
    }
  };

  const spin = (key, Icon) => (busy === key ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />);

  return (
    <section className="rounded-xl border border-[#E5E7EB] p-4" data-testid="contract-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-[#373A45]">
          <FileSignature size={16} className="text-[#965EA7]" /> {contractTitle(isFpedContract(state))}
        </div>
        <label className="flex items-center gap-2 text-xs text-[#6B7280]">
          <input type="checkbox" checked={probe} onChange={(e) => setProbe(e.target.checked)}
            className="h-4 w-4 accent-[#965EA7]" data-testid="contract-probe-toggle" />
          Mod de probă
        </label>
      </div>

      {probe && (
        <p className="mt-2 rounded-lg bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">
          Contractul pleacă la tine, nu la participant, cu marcajul PROBĂ pe fiecare pagină. Consumă 1 credit,
          iar semnătura semnatarului FPED aplicată la final e reală.
        </p>
      )}

      {state == null && (
        <div className="mt-2 flex items-center gap-2 text-sm text-[#6B7280]">
          <Loader2 size={14} className="animate-spin" /> Se verifică datele…
        </div>
      )}
      {state?.error && <p className="mt-2 text-sm text-[#9b1f2c]">{state.error}</p>}

      {state && !state.error && (
        <>
          <ContractCard contract={contract} onChanged={() => load(probe)} />
          {contract && (
            <button type="button" onClick={() => navigate(`/admin/contracts?contract=${contract.id}`)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#965EA7] hover:underline"
              data-testid="contract-open-register">
              <ExternalLink size={13} /> Istoric și toate contractele în registru
            </button>
          )}

          {!contract?.active && state.ready && (
            <div className="mt-2 flex items-start gap-2 text-sm text-[#2F6B4E]" data-testid="contract-ready">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              Toate datele necesare sunt complete. Contractul poate fi generat.
            </div>
          )}

          {!contract?.active && !state.ready && (
            <div className="mt-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3" data-testid="contract-blockers">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#8a6a1a]">
                <TriangleAlert size={15} /> Contractul nu poate fi generat încă:
              </div>
              <ul className="mt-1.5 list-disc pl-5 space-y-1 text-sm text-[#373A45]">
                {state.blockers.map((b) => <li key={b.code}>{b.message}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {actions.send && (
              <>
                <button type="button" onClick={preview} disabled={!state.ready || busy} className={BTN_SECONDARY}
                  data-testid="contract-preview-button">
                  {spin("preview", Eye)} Previzualizează
                </button>
                <button type="button" onClick={send} disabled={!state.ready || !state.can_send || busy}
                  className={BTN_PRIMARY} data-testid="contract-send-button"
                  title={state.can_send ? "" : "Lipsesc ESEMNEAZA_API_KEY sau ESEMNEAZA_SIGNER_EMAIL în Render."}>
                  {spin("send", Send)} {probe ? "Trimite de probă" : (contract ? "Trimite din nou" : "Trimite contractul")}
                </button>
              </>
            )}
          </div>
          <p className="mt-2 text-[11px] text-[#6B7280]">
            Previzualizarea și documentele conțin CNP-ul. Toate acțiunile sunt înregistrate în jurnalul de audit.
          </p>
        </>
      )}
    </section>
  );
}
